
import { useQuery } from 'react-query';
import { dashboardApi } from '../services/api';
import { 
  BarChart3, 
  TrendingUp, 
  HardDrive, 
  Video, 
  Download,
  Calendar,
  PieChart,
  Activity
} from 'lucide-react';

export default function Analytics() {
  const { data: stats, isLoading: statsLoading } = useQuery(
    'dashboard-stats',
    () => dashboardApi.getStats().then(res => res.data.data)
  );

  const { data: storage, isLoading: storageLoading } = useQuery(
    'dashboard-storage',
    () => dashboardApi.getStorageBreakdown().then(res => res.data.data)
  );

  const { data: activity, isLoading: activityLoading } = useQuery(
    'dashboard-activity',
    () => dashboardApi.getActivity(10).then(res => res.data.data)
  );

  if (statsLoading || storageLoading || activityLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/4 animate-pulse"></div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow h-64 animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
        <p className="mt-1 text-sm text-gray-500">
          Detailed insights into your VOD archive usage and storage
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Video className="h-8 w-8 text-twitch-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Total Videos
                </dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-gray-900">
                    {stats?.totalVideos || 0}
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <HardDrive className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Storage Used
                </dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-gray-900">
                    {stats?.totalStorageUsedFormatted || '0 Bytes'}
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Download className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Data Downloaded
                </dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-gray-900">
                    {stats?.dataInBytesFormatted || '0 Bytes'}
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Twitch VODs
                </dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-gray-900">
                    {stats?.totalVodsDownloaded || 0}
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Storage by Source */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <PieChart className="h-5 w-5 mr-2" />
              Storage by Source
            </h3>
          </div>
          
          {storage?.bySource.length ? (
            <div className="space-y-4">
              {storage.bySource.map((item, index) => {
                const colors = ['bg-twitch-600', 'bg-blue-600', 'bg-green-600', 'bg-purple-600'];
                
                return (
                  <div key={item.source}>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${colors[index % colors.length]} mr-2`}></div>
                        <span className="font-medium text-gray-900 capitalize">
                          {item.source}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">{item.sizeFormatted}</div>
                        <div className="text-xs text-gray-500">{item.count} files</div>
                      </div>
                    </div>
                    <div className="progress-bar">
                      <div
                        className={`h-2 rounded-full ${colors[index % colors.length]}`}
                        style={{
                          width: `${stats?.totalStorageUsed ? (item.size / stats.totalStorageUsed) * 100 : 0}%`
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <PieChart className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">No data available</p>
            </div>
          )}
        </div>

        {/* Storage by Status */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Storage by Status
            </h3>
          </div>
          
          {storage?.byStatus.length ? (
            <div className="space-y-4">
              {storage.byStatus.map((item) => {
                const statusColors = {
                  ready: 'bg-green-600',
                  processing: 'bg-yellow-600',
                  uploading: 'bg-blue-600',
                  error: 'bg-red-600',
                };
                
                return (
                  <div key={item.status}>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${statusColors[item.status as keyof typeof statusColors] || 'bg-gray-600'} mr-2`}></div>
                        <span className="font-medium text-gray-900 capitalize">
                          {item.status}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">{item.sizeFormatted}</div>
                        <div className="text-xs text-gray-500">{item.count} files</div>
                      </div>
                    </div>
                    <div className="progress-bar">
                      <div
                        className={`h-2 rounded-full ${statusColors[item.status as keyof typeof statusColors] || 'bg-gray-600'}`}
                        style={{
                          width: `${stats?.totalStorageUsed ? (item.size / stats.totalStorageUsed) * 100 : 0}%`
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">No data available</p>
            </div>
          )}
        </div>

        {/* Storage Tiers */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <HardDrive className="h-5 w-5 mr-2" />
              Storage Tiers
            </h3>
          </div>
          
          {storage?.byTier.length ? (
            <div className="space-y-4">
              {storage.byTier.map((item) => {
                const tierColors = {
                  regular: 'bg-blue-600',
                  'long-term': 'bg-purple-600',
                };
                
                return (
                  <div key={item.tier}>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${tierColors[item.tier as keyof typeof tierColors] || 'bg-gray-600'} mr-2`}></div>
                        <span className="font-medium text-gray-900 capitalize">
                          {item.tier.replace('-', ' ')}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">{item.sizeFormatted}</div>
                        <div className="text-xs text-gray-500">{item.count} files</div>
                      </div>
                    </div>
                    <div className="progress-bar">
                      <div
                        className={`h-2 rounded-full ${tierColors[item.tier as keyof typeof tierColors] || 'bg-gray-600'}`}
                        style={{
                          width: `${stats?.totalStorageUsed ? (item.size / stats.totalStorageUsed) * 100 : 0}%`
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <HardDrive className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">No data available</p>
            </div>
          )}
        </div>

        {/* Recent Activity Timeline */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Activity className="h-5 w-5 mr-2" />
              Recent Activity
            </h3>
          </div>
          
          {activity?.recentVods.length ? (
            <div className="space-y-3">
              {activity.recentVods.map((vod) => (
                <div key={vod.id} className="flex items-center space-x-3">
                  <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                    vod.source === 'twitch' ? 'bg-twitch-100' : 'bg-blue-100'
                  }`}>
                    {vod.source === 'twitch' ? (
                      <Download className="h-4 w-4 text-twitch-600" />
                    ) : (
                      <Video className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {vod.title}
                    </p>
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(vod.uploadedAt).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>{vod.fileSizeFormatted}</span>
                    </div>
                  </div>
                  <div className={`flex-shrink-0 px-2 py-1 text-xs rounded-full ${
                    vod.status === 'ready' ? 'bg-green-100 text-green-800' :
                    vod.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                    vod.status === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {vod.status}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">No recent activity</p>
            </div>
          )}
        </div>
      </div>

      {/* Account Information */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <dt className="text-sm font-medium text-gray-500">Account Type</dt>
            <dd className="mt-1 text-sm text-gray-900 capitalize">
              {stats?.role || 'Free'} Account
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Retention Period</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {stats?.retentionPeriodDays || 365} days
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Long-term Threshold</dt>
            <dd className="mt-1 text-sm text-gray-900">30 days</dd>
          </div>
        </div>
      </div>
    </div>
  );
}